import "@imi/ui/theme.css";
import "./sentry.ts";

import * as Sentry from "@sentry/react";
import { lazy, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { Provider as ReactReduxProvider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router";
import { appLoader } from "./App.loader.ts";
import { App } from "./App.tsx";
import { onSigninCallback } from "./core/auth/callbacks.ts";
import { oidcConfig, oidcStorage } from "./core/auth/oidcConfig.ts";
import { withProtectedRoute } from "./core/auth/withProtectedRoute.tsx";
import "./core/i18n/config.ts";
import { store } from "./core/store.ts";
import AuthLayout from "./routes/auth/AuthLayout.tsx";
import { selectionEditPageLoader } from "./routes/projects/$projectId/selections/$selectionId/SelectionEditPage.loader.ts";
import { TechnicalSelectionSubProductsLoader } from "./routes/projects/$projectId/selections/technical/$category/$subProductType/TechnicalSelectionSubProducts.loader.ts";
import AnalyticsProvider from "./shared/components/Analytics/AnalyticsProvider.tsx";
import { RootErrorBoundary } from "./shared/components/ErrorBoundaries/RootErrorBoundary.tsx";
import { FeatureFlagsProvider } from "./shared/components/FeatureFlags/FeatureFlagsProvider.tsx";
import { NotCompatibleBrowser } from "./shared/components/NotCompatibleBrowser.tsx";
import { SalesforceSilentRenew } from "./shared/components/SalesforceSilentRenew.tsx";
import { ToastContextProvider } from "./shared/components/Toast/ToastContextProvider.tsx";
import { isWebAssemblySupported } from "./shared/helpers/isWebAssemblySupported.ts";

const routes = {
  automaticSelectionSettings: lazy(
    () =>
      import("./routes/settings/automatic-selection/AutomaticSelectionSettingsPage.tsx"),
  ),
  batchSelection: lazy(
    () =>
      import("./routes/projects/$projectId/selections/batch/BatchSelectionPage.tsx"),
  ),
  batchSelectionCategory: lazy(
    () =>
      import("./routes/projects/$projectId/selections/batch/$category/BatchSelectionCategoryPage.tsx"),
  ),
  batchSelectionLayout: lazy(
    () =>
      import("./routes/projects/$projectId/selections/batch/BatchSelectionLayout.tsx"),
  ),
  directSelection: lazy(
    () =>
      import("./routes/projects/$projectId/selections/direct/DirectSelectionPage.tsx"),
  ),
  documentation: lazy(
    () =>
      import("./routes/documentation/technical-datasheets/TechnicalDatasheetsPage.tsx"),
  ),
  login: lazy(() => import("./routes/auth/login/LoginPage.tsx")),
  logout: lazy(() => import("./routes/auth/logout/LogoutPage.tsx")),
  notFound: lazy(() => import("./routes/not-found/NotFoundPage.tsx")),
  project: lazy(() => import("./routes/projects/$projectId/ProjectPage.tsx")),
  projectLayout: lazy(
    () => import("./routes/projects/$projectId/ProjectLayout.tsx"),
  ),
  projects: lazy(() => import("./routes/projects/ProjectsPage.tsx")),
  selectionEdit: lazy(
    () =>
      import("./routes/projects/$projectId/selections/$selectionId/SelectionEditPage.tsx"),
  ),
  silentRenew: lazy(
    () => import("./routes/auth/silent-renew/SilentRenewPage.tsx"),
  ),
  technicalSelection: lazy(
    () =>
      import("./routes/projects/$projectId/selections/technical/TechnicalSelectionPage.tsx"),
  ),
  technicalSelectionCategory: lazy(
    () =>
      import("./routes/projects/$projectId/selections/technical/$category/TechnicalSelectionCategoryPage.tsx"),
  ),
  technicalSelectionCategoryLayout: lazy(
    () =>
      import("./routes/projects/$projectId/selections/technical/$category/TechnicalSelectionCategoryLayout.tsx"),
  ),
  technicalSelectionSubProducts: lazy(
    () =>
      import("./routes/projects/$projectId/selections/technical/$category/$subProductType/TechnicalSelectionSubProductsPage.tsx"),
  ),
  technicalSettings: lazy(
    () => import("./routes/settings/technical/TechnicalSettingsPage.tsx"),
  ),
};

const router = createBrowserRouter([
  {
    children: [
      {
        children: [
          { Component: routes.projects, index: true },
          {
            Component: routes.documentation,
            path: "/documentation/technical-datasheets/*",
          },
          {
            children: [
              { Component: routes.projects, index: true },
              {
                children: [
                  {
                    Component: routes.project,
                    index: true,
                  },
                  {
                    children: [
                      {
                        Component: routes.selectionEdit,
                        loader: selectionEditPageLoader,
                        path: ":selectionId/edit",
                      },
                      {
                        children: [
                          {
                            Component: routes.batchSelection,
                            index: true,
                          },
                          {
                            Component: routes.batchSelectionCategory,
                            path: ":category",
                          },
                        ],
                        Component: routes.batchSelectionLayout,
                        path: "batch",
                      },
                      {
                        Component: routes.directSelection,
                        path: "direct/*",
                      },
                      {
                        Component: routes.technicalSelection,
                        path: "technical",
                      },
                      {
                        children: [
                          {
                            Component: routes.technicalSelectionCategory,
                            index: true,
                          },
                          {
                            Component: routes.technicalSelectionSubProducts,
                            loader: TechnicalSelectionSubProductsLoader,
                            path: ":subProductType",
                          },
                        ],
                        Component: routes.technicalSelectionCategoryLayout,
                        path: "technical/:category",
                      },
                    ],
                    path: "selections",
                  },
                ],
                Component: routes.projectLayout,
                path: ":projectId",
              },
            ],
            path: "projects",
          },
          {
            children: [
              { Component: () => <div />, index: true },
              { Component: routes.technicalSettings, path: "technical" },
              {
                Component: routes.automaticSelectionSettings,
                path: "automatic-selection",
              },
            ],
            path: "settings",
          },
        ],
        Component: isWebAssemblySupported()
          ? withProtectedRoute(App)
          : NotCompatibleBrowser,
        loader: appLoader,
        path: "/",
      },
      {
        children: [
          { Component: routes.login, path: "login" },
          { Component: routes.logout, path: "logout" },
          { Component: routes.silentRenew, path: "silent-renew" },
        ],
        Component: AuthLayout,
        path: "auth",
      },
      { Component: routes.notFound, path: "*" },
    ],
    ErrorBoundary: RootErrorBoundary,
    loader: () => {
      // TODO - When going back to salesforce login, remove this
      const oidcKey = Object.keys(oidcStorage).find(
        (key) =>
          key.startsWith("oidc.user:https://imi-hydronic.my.site.com") ||
          key.startsWith(
            "oidc.user:https://imi-hydronic--uat.sandbox.my.site.com",
          ) ||
          key.startsWith(
            "oidc.user:https://imi-hydronic--ip.sandbox.my.site.com",
          ),
      );
      if (oidcKey) oidcStorage.removeItem(oidcKey);

      return {};
    },
    path: "/",
  },
]);

createRoot(document.getElementById("root")!, {
  onCaughtError: Sentry.reactErrorHandler(),
  onUncaughtError: Sentry.reactErrorHandler(),
}).render(
  <StrictMode>
    <AnalyticsProvider>
      <AuthProvider
        automaticSilentRenew
        onSigninCallback={onSigninCallback}
        {...oidcConfig}
      >
        <FeatureFlagsProvider
          flags={[
            {
              enabled: true,
              name: "authentication",
            },
            {
              enabled: false,
              name: "settings",
            },
          ]}
        >
          <SalesforceSilentRenew>
            <ReactReduxProvider store={store}>
              <ToastContextProvider>
                <RouterProvider router={router} />
              </ToastContextProvider>
            </ReactReduxProvider>
          </SalesforceSilentRenew>
        </FeatureFlagsProvider>
      </AuthProvider>
    </AnalyticsProvider>
  </StrictMode>,
);
